import { useNavigate } from 'react-router-dom';
import { addToast, Button, Modal, ModalBody, ModalContent } from '@heroui/react';
import { useStartPersonalChat } from '@/features/Chat';
import { useModalAutoFocus } from '@/shared/lib/hooks';
import { routerPath } from '@/shared/config/router';
import { Svg } from '@/shared/ui/Svg';
import CloseIcon from '@/shared/assets/icons/close.svg';
import MailIcon from '@/shared/assets/icons/mail.svg';
import UserVoiceIcon from '@/shared/assets/icons/user-voice.svg';

const SUPPORT_EMAIL = 'info@ihcl.org.uk';
const SUPPORT_PHONE = '+44 203 807 45 97';
const SUPPORT_SUBJECT = 'IHCL LMS Support';
const SUPPORT_MAILTO = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(SUPPORT_SUBJECT)}`;
const SUPPORT_COMPOSE_URL = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(SUPPORT_EMAIL)}&su=${encodeURIComponent(SUPPORT_SUBJECT)}`;

type SupportModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const SupportModal = ({ isOpen, onClose }: SupportModalProps) => {
  const navigate = useNavigate();
  const { startSupportChat, isStarting } = useStartPersonalChat();
  useModalAutoFocus(isOpen);

  const handleOpenChat = async () => {
    try {
      const chat = await startSupportChat();
      onClose();
      navigate(chat?.id ? `${routerPath.chats}?chat=${chat.id}` : routerPath.chats);
    } catch {
      addToast({
        title: 'Could not open support chat',
        color: 'danger',
      });
    }
  };

  const handleSendEmail = () => {
    window.open(SUPPORT_COMPOSE_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      placement="center"
      hideCloseButton
      scrollBehavior="inside"
      classNames={{
        base: 'mx-4 w-full max-w-[420px] rounded-[16px] bg-white shadow-[0_24px_48px_rgba(30,30,30,0.12)]',
        body: 'p-0',
        wrapper: 'px-2',
        backdrop: 'bg-black/30',
      }}
    >
      <ModalContent>
        <ModalBody>
          <div className="relative px-6 py-7 sm:px-8">
            <button
              type="button"
              className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-full text-[#757575] transition-colors hover:bg-black/[0.04] hover:text-[#1E1E1E] sm:right-5 sm:top-5"
              onClick={onClose}
              aria-label="Close support modal"
            >
              <Svg Icon={CloseIcon} className="h-[18px] w-[18px]" />
            </button>

            <h2 className="pr-10 text-[28px] font-medium leading-[1.2] text-[#1E1E1E]">Support</h2>
            <p className="mt-3 text-sm leading-5 text-grey-secondary">
              Choose how you would like to get in touch. Our team is here to help.
            </p>

            <div className="mt-6 flex flex-col">
              <div className="border-t border-[#ECECEC] py-5">
                <div className="flex items-start gap-3">
                  <Svg Icon={UserVoiceIcon} className="mt-0.5 h-6 w-6 shrink-0 text-red" />
                  <div className="min-w-0 flex-1">
                    <p className="text-base font-semibold text-[#1E1E1E]">Chat</p>
                    <p className="mt-1 text-sm text-grey-secondary">
                      Message an administrator in the LMS chat.
                    </p>
                    <Button
                      radius="full"
                      className="mt-4 h-11 w-full bg-red text-sm font-medium text-white"
                      isLoading={isStarting}
                      isDisabled={isStarting}
                      onPress={handleOpenChat}
                    >
                      Open chat
                    </Button>
                  </div>
                </div>
              </div>

              <div className="border-t border-[#ECECEC] py-5">
                <div className="flex items-start gap-3">
                  <Svg Icon={MailIcon} className="mt-0.5 h-6 w-6 shrink-0 text-red" />
                  <div className="min-w-0 flex-1">
                    <p className="text-base font-semibold text-[#1E1E1E]">Email</p>
                    <p className="mt-1 text-sm text-grey-secondary">
                      Send us an email or call during office hours.
                    </p>
                    <div className="mt-3 space-y-1 text-sm text-[#1E1E1E]">
                      <p>
                        Email:{' '}
                        <a href={SUPPORT_MAILTO} className="underline hover:text-red">
                          {SUPPORT_EMAIL}
                        </a>
                      </p>
                      <p>Phone: {SUPPORT_PHONE}</p>
                    </div>
                    <Button
                      radius="full"
                      className="mt-4 h-11 w-full bg-red text-sm font-medium text-white"
                      onPress={handleSendEmail}
                    >
                      Send Email
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
